import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const {
  FiFolder,
  FiCornerDownRight,
  FiX,
  FiSearch,
  FiChevronRight,
  FiHome,
  FiCheck,
  FiLoader,
  FiShield,
} = FiIcons;

/**
 * Builds a flat list of all folders across the whole app with hierarchical path information.
 */
function getAllFoldersTree(data, itemsToMove = []) {
  if (!data) return [];

  const movingFolderIds = new Set();
  for (const it of itemsToMove) {
    if (it?.type === 'folder') {
      const fId = it.targetId || it._dbFolderId || it.id;
      if (fId) movingFolderIds.add(fId);
    }
  }

  // Set of all moving folder IDs and their descendants
  const descendantIds = new Set(movingFolderIds);
  if (movingFolderIds.size > 0) {
    let added = true;
    while (added) {
      added = false;
      for (const [fId, fObj] of Object.entries(data)) {
        if (!descendantIds.has(fId) && fObj?.parentId && descendantIds.has(fObj.parentId)) {
          descendantIds.add(fId);
          added = true;
        }
      }
    }
  }

  // Helper to compute breadcrumb path from root
  const computePath = (folderId) => {
    if (folderId === 'root') return ['Root'];
    const parts = [];
    let curId = folderId;
    const visited = new Set();
    while (curId && curId !== 'root') {
      if (visited.has(curId)) break;
      visited.add(curId);
      const curObj = data[curId];
      if (!curObj) break;
      parts.unshift(curObj.title || 'Untitled');
      curId = curObj.parentId;
    }
    return parts;
  };

  const results = [];

  // 1. Root Folder
  const rootObj = data.root || {};
  const rootCount = (rootObj.items || []).length;
  results.push({
    id: 'root',
    title: 'Root (Top Level)',
    pathString: 'Root',
    depth: 0,
    itemCount: rootCount,
    isRoot: true,
    color: '#10b981',
    disabled: false,
    disabledReason: null,
  });

  // 2. All other folders
  for (const [folderId, folderObj] of Object.entries(data)) {
    if (folderId === 'root' || !folderObj) continue;

    const pathArray = computePath(folderId);
    const pathString = pathArray.join(' / ');
    const depth = pathArray.length;
    const itemCount = (folderObj.items || []).length;

    let disabled = false;
    let disabledReason = null;

    // Permissions check
    if (folderObj._isShared && folderObj._sharedPermission !== 'editor') {
      disabled = true;
      disabledReason = 'View-only folder';
    }

    // Cycle check if moving folders
    if (movingFolderIds.size > 0) {
      if (movingFolderIds.has(folderId)) {
        disabled = true;
        disabledReason = 'Cannot move folder into itself';
      } else if (descendantIds.has(folderId)) {
        disabled = true;
        disabledReason = 'Cannot move folder into its own subfolder';
      }
    }

    results.push({
      id: folderId,
      title: folderObj.title || 'Untitled Folder',
      pathArray,
      pathString,
      depth,
      itemCount,
      isRoot: false,
      color: folderObj.color || '#3b82f6',
      disabled,
      disabledReason,
    });
  }

  // Sort: Root first, then alphabetically by path
  results.sort((a, b) => {
    if (a.isRoot) return -1;
    if (b.isRoot) return 1;
    return a.pathString.localeCompare(b.pathString);
  });

  return results;
}

export default function MoveToModal({
  isOpen,
  onClose,
  item,
  items = [],
  currentFolderId = 'root',
  data = {},
  onMove,
  themeColors: tc = {},
  isDark = true,
}) {
  const [search, setSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [moving, setMoving] = useState(false);
  const searchInputRef = useRef(null);

  const targetItems = useMemo(() => {
    if (items && items.length > 0) return items;
    if (item) return [item];
    return [];
  }, [item, items]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedFolderId(null);
      setMoving(false);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen, item, items]);

  // Compute all available destination folders
  const allFolders = useMemo(() => {
    if (!isOpen || targetItems.length === 0) return [];
    return getAllFoldersTree(data, targetItems);
  }, [isOpen, targetItems, data]);

  // Filter folders by search query
  const filteredFolders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allFolders;
    return allFolders.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.pathString.toLowerCase().includes(q)
    );
  }, [allFolders, search]);

  if (!isOpen || targetItems.length === 0) return null;

  const handleSelectAndMove = async (targetFolderId) => {
    if (moving) return;
    if (targetFolderId === currentFolderId) return;

    setMoving(true);
    setSelectedFolderId(targetFolderId);
    try {
      playSound('launch');
      await onMove(targetFolderId, targetItems);
      onClose();
    } catch (err) {
      console.error('Failed to move item:', err);
      alert('Failed to move: ' + (err.message || 'Unknown error'));
      setMoving(false);
    }
  };

  const isMultiple = targetItems.length > 1;
  const isFolder = !isMultiple && targetItems[0]?.type === 'folder';
  const modalTitle = isMultiple
    ? `Move ${targetItems.length} Items`
    : `Move ${isFolder ? 'Folder' : 'Bookmark'}`;
  const modalSubtitle = isMultiple
    ? `Select destination for ${targetItems.length} selected items across app`
    : `Select destination for "${targetItems[0]?.title || ''}"`;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => { if (!moving) onClose(); }}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        {/* Modal Dialog Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border z-10 flex flex-col"
          style={{
            backgroundColor: isDark ? 'rgba(18, 22, 25, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)',
            boxShadow: `0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 30px ${tc.accent || '#10b981'}25`,
            maxHeight: '85vh',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4 border-b"
            style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: `${tc.accent || '#10b981'}20`,
                  border: `1px solid ${tc.accent || '#10b981'}40`,
                }}
              >
                <SafeIcon
                  icon={FiCornerDownRight}
                  className="w-5 h-5"
                  style={{ color: tc.accent || '#10b981' }}
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold truncate" style={{ color: tc.text || '#fff' }}>
                  {modalTitle}
                </h3>
                <p className="text-xs truncate opacity-70" style={{ color: tc.muted || '#94a3b8' }}>
                  {modalSubtitle}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              disabled={moving}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-neutral-400 hover:text-white"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Search Filter Box */}
          <div
            className="p-3 border-b"
            style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)' }}
          >
            <div className="relative">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search destination folders across app..."
                className="w-full pl-10 pr-9 py-2 rounded-xl text-sm outline-none transition-all border"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.04)',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                  color: tc.text || '#fff',
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white text-xs"
                >
                  <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Folder List */}
          <div className="overflow-y-auto p-3 space-y-1 flex-1 min-h-[220px] max-h-[380px] custom-scrollbar">
            {filteredFolders.map((folder) => {
              const isCurrent = folder.id === currentFolderId;
              const isSelected = selectedFolderId === folder.id;
              const isDisabled = folder.disabled || isCurrent || moving;

              return (
                <motion.button
                  key={folder.id}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleSelectAndMove(folder.id)}
                  whileHover={!isDisabled ? { x: 3 } : undefined}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-left text-sm transition-all border ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-500/20'
                      : isCurrent
                      ? 'border-neutral-700/50 bg-white/5 opacity-50 cursor-not-allowed'
                      : folder.disabled
                      ? 'border-transparent opacity-40 cursor-not-allowed'
                      : 'border-transparent hover:border-white/10 hover:bg-white/10 cursor-pointer'
                  }`}
                  style={{
                    backgroundColor: isSelected
                      ? `${tc.accent || '#10b981'}25`
                      : undefined,
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0 pr-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: folder.isRoot
                          ? 'rgba(16, 185, 129, 0.15)'
                          : `${folder.color || tc.accent || '#3b82f6'}20`,
                        border: `1px solid ${
                          folder.isRoot
                            ? 'rgba(16, 185, 129, 0.3)'
                            : `${folder.color || tc.accent || '#3b82f6'}40`
                        }`,
                      }}
                    >
                      <SafeIcon
                        icon={folder.isRoot ? FiHome : FiFolder}
                        className="w-4 h-4"
                        style={{
                          color: folder.isRoot
                            ? '#10b981'
                            : folder.color || tc.accent || '#3b82f6',
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-medium truncate"
                          style={{ color: tc.text || '#fff' }}
                        >
                          {folder.title}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300 border border-neutral-700">
                            Current Location
                          </span>
                        )}
                        {folder.disabledReason && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                            {folder.disabledReason}
                          </span>
                        )}
                      </div>

                      <div
                        className="text-xs truncate opacity-65 flex items-center gap-1 mt-0.5"
                        style={{ color: tc.muted || '#94a3b8' }}
                      >
                        <span>{folder.pathString}</span>
                        <span>•</span>
                        <span>{folder.itemCount} items</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {moving && isSelected ? (
                      <SafeIcon
                        icon={FiLoader}
                        className="w-4 h-4 animate-spin text-emerald-400"
                      />
                    ) : !isDisabled ? (
                      <span
                        className="text-xs font-semibold px-2 py-1 rounded-lg transition-colors border"
                        style={{
                          backgroundColor: `${tc.accent || '#10b981'}15`,
                          borderColor: `${tc.accent || '#10b981'}30`,
                          color: tc.accent || '#10b981',
                        }}
                      >
                        Move Here
                      </span>
                    ) : null}
                  </div>
                </motion.button>
              );
            })}

            {filteredFolders.length === 0 && (
              <div className="py-12 text-center text-sm text-neutral-400">
                No folders match <span className="font-semibold text-white">"{search}"</span>
              </div>
            )}
          </div>

          {/* Footer note */}
          <div
            className="px-5 py-3 border-t flex items-center justify-between text-xs"
            style={{
              borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
              color: tc.muted || '#94a3b8',
            }}
          >
            <span>Click any folder to instantly move the item</span>
            <button
              onClick={onClose}
              disabled={moving}
              className="px-3 py-1 rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
